import React, { useMemo, useRef, useState } from "react";
import { Users, X, Plus, Check } from "lucide-react";

/**
 * Multi-assignee picker for Q&A: chip-style selected pills + dropdown with
 * search + "Assign to all admins" toggle. Designed to scale to a few dozen
 * members without listing them all at once.
 *
 * Props:
 *  - options       Array<{user_id, name, email, role}> — the pool to pick from
 *  - selectedIds   string[] — currently picked user ids
 *  - assignAll     boolean  — "all admins" semantic flag (overrides selectedIds)
 *  - onChange      ({ ids, assignAll }) => void
 *  - dense         boolean  — compact mode used inside the per-row admin panel
 *  - placeholder   string   — input placeholder when empty
 *  - testId        string   — base data-testid (modal vs row variants stay distinct)
 */
export default function MultiAssigneePicker({
  options, selectedIds = [], assignAll = false, onChange,
  dense = false, placeholder = "Add assignee…", testId = "multi-assignee",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  // Close the dropdown when clicking outside the picker.
  React.useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const byId = useMemo(() => {
    const m = new Map();
    (options || []).forEach((o) => m.set(o.user_id, o));
    return m;
  }, [options]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (options || []).filter((o) => {
      if (selectedIds.includes(o.user_id)) return false;
      if (!needle) return true;
      return (o.name || "").toLowerCase().includes(needle)
          || (o.email || "").toLowerCase().includes(needle);
    });
  }, [options, q, selectedIds]);

  const add = (id) => {
    if (selectedIds.includes(id)) return;
    onChange?.({ ids: [...selectedIds, id], assignAll: false });
    setQ("");
  };
  const remove = (id) => {
    onChange?.({ ids: selectedIds.filter((x) => x !== id), assignAll });
  };
  const toggleAll = () => {
    onChange?.({ ids: assignAll ? selectedIds : [], assignAll: !assignAll });
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      {/* Chips + input */}
      <div className={`flex flex-wrap gap-1 ${dense ? "min-h-[34px] px-2 py-1.5" : "min-h-[40px] px-3 py-2"} bg-background border border-border rounded-md focus-within:ring-2 focus-within:ring-ring`}>
        {assignAll ? (
          <span data-testid={`${testId}-all-chip`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 mono text-[11px] uppercase tracking-wider">
            <Users className="w-3 h-3" /> All admins
            <button type="button" onClick={toggleAll} aria-label="Remove all-admins" className="ml-1 hover:opacity-70">
              <X className="w-3 h-3" />
            </button>
          </span>
        ) : (
          selectedIds.map((id) => {
            const o = byId.get(id);
            const label = o ? (o.name || o.email) : id;
            return (
              <span key={id} data-testid={`${testId}-chip-${id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/30 text-xs">
                {label}
                <button type="button" onClick={() => remove(id)} aria-label={`Remove ${label}`} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })
        )}
        {!assignAll && (
          <input
            data-testid={`${testId}-search`}
            value={q}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            placeholder={selectedIds.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[100px] bg-transparent outline-none text-xs"
          />
        )}
      </div>

      {/* Dropdown */}
      {open && !assignAll && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto bg-card border border-border rounded-md shadow-lg">
          <button
            type="button"
            onClick={toggleAll}
            data-testid={`${testId}-toggle-all`}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-secondary transition border-b border-border"
          >
            <Users className="w-3.5 h-3.5 text-emerald-500" />
            <span className="flex-1 text-left font-medium">Assign to all admins</span>
            <span className="mono text-[10px] text-muted-foreground">notifies everyone</span>
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground text-center">
              {q ? "No matches" : "Everyone is already picked"}
            </div>
          ) : filtered.map((o) => (
            <button
              key={o.user_id}
              type="button"
              onClick={() => add(o.user_id)}
              data-testid={`${testId}-option-${o.user_id}`}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-secondary transition"
            >
              <Plus className="w-3 h-3 text-muted-foreground" />
              <span className="flex-1 text-left truncate">
                <span className="font-medium">{o.name || o.email}</span>
                {o.name && o.email && (
                  <span className="text-muted-foreground"> · {o.email}</span>
                )}
              </span>
              {o.role === "admin" && (
                <span className="mono uppercase tracking-wider text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/30">admin</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
